import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { SideNavGroup, SideNavItem } from './side-navbar.models';

@Component({
	selector: 'app-side-navbar',
	templateUrl: './side-navbar.component.html',
	styleUrls: ['./side-navbar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavbarComponent implements OnChanges, OnInit, OnDestroy {
	// ─── Inputs ────────────────────────────────────────────────────────────
	@Input() groups: SideNavGroup[] = [];
	@Input() activeId: string = '';
	@Input() loading: boolean = false;
	@Input() collapsed: boolean = false;

	// ─── Outputs ───────────────────────────────────────────────────────────
	@Output() itemSelected = new EventEmitter<SideNavItem>();
	@Output() favoriteToggled = new EventEmitter<SideNavItem>();

	// ─── ViewChild ─────────────────────────────────────────────────────────
	@ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

	// ─── State ─────────────────────────────────────────────────────────────
	expandedItems = new Set<string>();
	expandedGroups = new Set<string>();

	/** Precomputed set of item ids that have an active child — avoids iterating children in the template. */
	parentActiveIds = new Set<string>();

	/** Precomputed set of group ids that contain the active item — avoids iterating items in the template. */
	activeGroupIds = new Set<string>();

	/** Cached collapsed state — avoids a getter being called multiple times per change detection. */
	isCollapsed: boolean = false;

	/** Current search term entered by the user. */
	searchTerm: string = '';

	/** Whether a search is currently active. */
	searchInProgress: boolean = false;

	/** Groups filtered by the current search term. */
	filteredGroups: SideNavGroup[] = [];

	// ─── Private fields ────────────────────────────────────────────────────
	private readonly destroy$ = new Subject<void>();
	private readonly searchInput$ = new Subject<string>();
	private lastAppliedSearchTerm: string | null = null;
	private routerSub: Subscription | null = null;

	constructor(
		private router: Router,
		private cdr: ChangeDetectorRef,
		private ngZone: NgZone
	) {}

	// ─── Lifecycle ─────────────────────────────────────────────────────────

	/** Subscribes to router events to sync activeId, and sets up debounced search — both outside Angular zone. */
	ngOnInit(): void {
		this.ngZone.runOutsideAngular(() => {
			this.routerSub = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
				this.syncActiveFromRoute();
				this.cdr.detectChanges();
			});

			this.searchInput$.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((term) => {
				this.applySearchFilter(term);
				this.cdr.detectChanges();
			});
		});
	}

	/** Auto-expand the parent group containing the active item on changes, rebuild precomputed sets. */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['activeId'] || changes['groups']) {
			if (changes['groups']) {
				this.lastAppliedSearchTerm = null;
				this.syncActiveFromRoute();
				this.applySearchFilter(this.searchTerm);
			}
			this.autoExpandActiveParent();
			this.rebuildActiveState();
		}
		this.cdr.detectChanges();
	}

	/** Cleans up subscriptions and subjects. */
	ngOnDestroy(): void {
		this.routerSub?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ─── Public actions ────────────────────────────────────────────────────

	/** Toggles group expansion state — expands sidebar if collapsed, otherwise toggles the group. */
	toggleGroup(group: SideNavGroup): void {
		if (this.isCollapsed) {
			this.clickExpanded = true;
			this.expandedGroups.add(group.id);
			this.updateCollapsedState();
			this.cdr.detectChanges();
			return;
		}

		if (this.expandedGroups.has(group.id)) {
			this.expandedGroups.delete(group.id);
		} else {
			this.expandedGroups.add(group.id);
		}
		this.cdr.detectChanges();
	}

	/** Handles click on a nav item — toggles children or emits selection. */
	onItemClick(item: SideNavItem): void {
		if (item.children && item.children.length > 0) {
			this.toggleExpand(item.id);
		} else {
			this.activeId = item.id;
			this.rebuildActiveState();
		}
		this.itemSelected.emit(item);
		this.cdr.detectChanges();
	}

	/** Handles click on a child sub-item — emits selection. */
	onSubItemClick(item: SideNavItem): void {
		this.activeId = item.id;
		this.rebuildActiveState();
		this.itemSelected.emit(item);
		this.cdr.detectChanges();
	}

	/** Toggles the favorite state of an item and emits the event. */
	onFavoriteToggle(item: SideNavItem, event: MouseEvent): void {
		event.stopPropagation();
		item.isFavorite = !item.isFavorite;
		this.favoriteToggled.emit(item);
		this.cdr.detectChanges();
	}

	// ─── Search ──────────────────────────────────────────────────────────────

	/** Pushes the current search term into the debounced subject. */
	onSearchKeyUp(): void {
		this.searchInProgress = this.searchTerm.length > 0;
		this.searchInput$.next(this.searchTerm);
		this.cdr.detectChanges();
	}

	/** Clears the search term and immediately resets the filtered groups. */
	clearSearch(): void {
		this.searchTerm = '';
		this.searchInProgress = false;
		this.applySearchFilter('');
		this.cdr.detectChanges();
	}

	/** Expands the sidebar and focuses the search input. */
	expandAndFocusSearch(): void {
		this.userCollapsed = false;
		this.clickExpanded = false;
		this.updateCollapsedState();
		this.cdr.detectChanges();
		setTimeout(() => this.searchInputRef?.nativeElement?.focus());
	}

	// ─── Collapse / expand handlers ───────────────────────────────────────

	/** Toggles the user-driven collapsed state via the collapse/expand button. */
	toggleCollapse(): void {
		if (this.clickExpanded) {
			this.clickExpanded = false;
		} else {
			this.userCollapsed = !this.userCollapsed;
		}
		if (this.userCollapsed) {
			this.expandedGroups.clear();
		}
		this.updateCollapsedState();
		this.cdr.detectChanges();
	}

	/** Collapses the sidebar back when the mouse leaves (only when temporarily expanded via click). */
	onMouseLeave(): void {
		if (this.clickExpanded) {
			this.clickExpanded = false;
			this.expandedGroups.clear();
			this.updateCollapsedState();
			this.cdr.detectChanges();
		}
	}

	// ─── Private helpers ───────────────────────────────────────────────────

	/** Whether the user explicitly collapsed the sidebar via the toggle button. */
	private userCollapsed = false;

	/** Whether the sidebar is temporarily expanded due to clicking a group while collapsed. */
	private clickExpanded = false;

	/** Recomputes the cached isCollapsed boolean from userCollapsed and clickExpanded. */
	private updateCollapsedState(): void {
		this.isCollapsed = this.userCollapsed && !this.clickExpanded;
	}

	/** Rebuilds the parentActiveIds and activeGroupIds sets based on the current activeId. */
	private rebuildActiveState(): void {
		this.parentActiveIds.clear();
		this.activeGroupIds.clear();

		for (const group of this.groups) {
			for (const item of group.items) {
				if (item.id === this.activeId) {
					this.activeGroupIds.add(group.id);
				}
				if (item.children && item.children.some((child) => child.id === this.activeId)) {
					this.parentActiveIds.add(item.id);
					this.activeGroupIds.add(group.id);
				}
			}
		}
	}

	/**
	 * Filters groups and items based on the given search term, expanding all matching groups.
	 * Skips re-filtering if the normalized term hasn't changed since last application.
	 */
	private applySearchFilter(rawTerm: string): void {
		const term = rawTerm.trim().toLowerCase();

		if (term === this.lastAppliedSearchTerm) {
			return;
		}
		this.lastAppliedSearchTerm = term;

		if (!term) {
			this.filteredGroups = this.groups;
			return;
		}

		this.filteredGroups = this.groups
			.map((group) => {
				const matchingItems = group.items
					.map((item) => {
						const itemMatches = item.label.toLowerCase().includes(term);
						if (itemMatches) {
							return item;
						}

						const childMatches = item.children?.filter((child) => child.label.toLowerCase().includes(term)) ?? [];
						if (childMatches.length > 0) {
							return { ...item, children: childMatches };
						}
						return null;
					})
					.filter((item): item is SideNavItem => item !== null);

				if (matchingItems.length === 0) {
					return null;
				}
				return { ...group, items: matchingItems };
			})
			.filter((group): group is SideNavGroup => group !== null);

		for (const group of this.filteredGroups) {
			this.expandedGroups.add(group.id);
			for (const item of group.items) {
				if (item.children && item.children.length > 0) {
					this.expandedItems.add(item.id);
				}
			}
		}
	}

	/** Toggles the expanded state for the given item id. */
	private toggleExpand(id: string): void {
		if (this.expandedItems.has(id)) {
			this.expandedItems.delete(id);
		} else {
			this.expandedItems.add(id);
		}
	}

	/** Expands the parent group and item that contains the currently active child. */
	private autoExpandActiveParent(): void {
		for (const group of this.groups) {
			for (const item of group.items) {
				if (item.id === this.activeId) {
					this.expandedGroups.add(group.id);
				}
				if (item.children && item.children.some((child) => child.id === this.activeId)) {
					this.expandedGroups.add(group.id);
					this.expandedItems.add(item.id);
				}
			}
		}
	}

	/** Matches the current router URL against items' routerLink to set activeId. */
	private syncActiveFromRoute(): void {
		const url = this.router.url.split('?')[0];

		for (const group of this.groups) {
			for (const item of group.items) {
				if (item.routerLink && url.includes(item.routerLink)) {
					this.activeId = item.id;
					this.autoExpandActiveParent();
					this.rebuildActiveState();
					return;
				}
				if (item.children) {
					for (const child of item.children) {
						if (child.routerLink && url.includes(child.routerLink)) {
							this.activeId = child.id;
							this.autoExpandActiveParent();
							this.rebuildActiveState();
							return;
						}
					}
				}
			}
		}

		// No matching route found (e.g. home page) — clear active state
		this.activeId = '';
		this.rebuildActiveState();
	}
}
